/**
 * Editor operations for the attachment-backed card / comment `image`
 * node — shared by `CardCreateModal`, `CardDetailModal`, and the
 * `<MarkdownEditor>` toolbar upload button.
 *
 * The drop hook (`useCardDescriptionDrop`) and the toolbar own the upload
 * lifecycle; these helpers own the in-editor node lifecycle:
 *
 *   - `insertCardImagePlaceholder` — insert an in-body image at the caret
 *     showing the local blob preview + spinner, and return a placeholder
 *     id (or `null` when the editor isn't mounted / editable).
 *   - `resolveCardImagePlaceholder` — swap the placeholder for the
 *     resolved attachment (durable `attachmentId` + fresh signed `src`)
 *     once the upload lands.
 *   - `markCardImagePlaceholderFailed` — flip the placeholder into (or out
 *     of) its in-place error state (Retry / Dismiss) when the upload fails;
 *     the node stays in the body so the author can act on it.
 *   - `failCardImagePlaceholder` — remove the placeholder outright (the
 *     card description surfaces on failure; the comment composers on
 *     Dismiss / cancel).
 *
 * The `image` node is INLINE (lives inside a paragraph), so a dropped
 * image needs NO flanking empty paragraphs — the caret sits before/after
 * it within its own paragraph (arrow keys work; no gap cursor; no extra
 * space around the image when rendered). Inserts just drop the node at the
 * caret.
 */

import type { Editor } from '@tiptap/react'

import { collapseNodeSelectionAfter } from '../node-selection'

export interface CardImageResolveResult {
  attachmentId: string
  /** Freshly-resolved signed GET URL — a valid `https` URL, never a blob. */
  src: string
  alt: string
}

let placeholderCounter = 0

function makePlaceholderId(): string {
  placeholderCounter += 1
  return `card-img-${Date.now()}-${placeholderCounter}`
}

/**
 * Insert the BLOCK `image` node at the caret, then guarantee a trailing
 * paragraph so the caret has a text line to type in below a trailing
 * image. The image is block-level → it sits tight (no line-box leading,
 * `my-1` margin) with no flanking empty paragraphs. No LEADING paragraph:
 * the common case has text above already, and the exact top-edge caret is
 * covered by ProseMirror's gap cursor.
 */
function insertImageBlock(editor: Editor, imageAttrs: Record<string, unknown>): void {
  editor
    .chain()
    .focus()
    // Guard BEFORE the insert. `.focus()` on a body whose first node is
    // this very block atom resolves to a NodeSelection on it — no visible
    // caret — and `insertContent` REPLACES a selected node. Dropping an
    // image straight after opening the editor therefore destroyed the
    // image already there, and if that upload then failed the placeholder
    // was removed too, leaving the description empty.
    .command(({ tr }) => {
      collapseNodeSelectionAfter(tr)
      return true
    })
    .insertContent({ type: 'image', attrs: imageAttrs })
    // Guard AFTER the insert: `insertContent` leaves the new block atom
    // node-selected, so the next file of a multi-file drop would replace
    // the placeholder just added.
    .command(({ tr }) => {
      collapseNodeSelectionAfter(tr)
      return true
    })
    .command(({ tr, dispatch }) => {
      const { doc } = tr
      const paragraphType = doc.type.schema.nodes.paragraph
      if (paragraphType && doc.lastChild && doc.lastChild.type.name !== 'paragraph') {
        if (dispatch) tr.insert(doc.content.size, paragraphType.create())
      }
      return true
    })
    .run()
}

/**
 * Insert a durable image node with the given attrs. Used by the toolbar
 * upload path (which already has the resolved `attachmentId` + `src`).
 */
export function insertCardImage(
  editor: Editor | null,
  attrs: { src: string; alt?: string; attachmentId?: string },
): void {
  if (!editor || editor.isDestroyed || !editor.isEditable) return
  insertImageBlock(editor, {
    src: attrs.src,
    alt: attrs.alt || null,
    ...(attrs.attachmentId ? { attachmentId: attrs.attachmentId } : {}),
  })
}

/**
 * Insert an in-body image placeholder rendering the local `blobUrl` +
 * spinner. Returns the placeholder id, or `null` when the editor isn't
 * mounted / editable (the caller falls back to the attach-only pending
 * chip).
 */
export function insertCardImagePlaceholder(editor: Editor | null, blobUrl: string): string | null {
  if (!editor || editor.isDestroyed || !editor.isEditable) return null
  const placeholderId = makePlaceholderId()
  insertImageBlock(editor, {
    src: null,
    alt: null,
    uploadingBlobUrl: blobUrl,
    placeholderId,
  })
  return placeholderId
}

/**
 * Swap a placeholder for its resolved attachment. Locates the node fresh
 * inside the transaction so an intervening edit (the upload is async)
 * doesn't stale the position.
 */
export function resolveCardImagePlaceholder(
  editor: Editor | null,
  placeholderId: string,
  result: CardImageResolveResult,
): void {
  if (!editor || editor.isDestroyed) return
  editor
    .chain()
    .command(({ tr, state, dispatch }) => {
      let targetPos = -1
      let targetAttrs: Record<string, unknown> | null = null
      state.doc.descendants((n, pos) => {
        if (targetPos !== -1) return false
        if (n.type.name === 'image' && n.attrs.placeholderId === placeholderId) {
          targetPos = pos
          targetAttrs = n.attrs
          return false
        }
        return true
      })
      if (targetPos === -1 || !targetAttrs) return false
      // TS control-flow can't see the assignment inside the `descendants`
      // callback, so it still treats `targetAttrs` as `null` here despite
      // the guard above — cast past it.
      const attrs = targetAttrs as Record<string, unknown>
      if (dispatch) {
        tr.setNodeMarkup(targetPos, undefined, {
          ...attrs,
          attachmentId: result.attachmentId,
          src: result.src,
          alt: result.alt || null,
          uploadingBlobUrl: null,
          placeholderId: null,
        })
      }
      return true
    })
    .run()
}

/**
 * Flip a placeholder's error state. `true` after the upload fails (the
 * NodeView renders the dimmed preview + Retry / Dismiss), `false` when a
 * Retry re-runs the upload against the SAME node. Located fresh inside
 * the transaction; a no-op when the author already deleted the node.
 */
export function markCardImagePlaceholderFailed(
  editor: Editor | null,
  placeholderId: string,
  failed: boolean,
): void {
  if (!editor || editor.isDestroyed) return
  editor
    .chain()
    .command(({ tr, state, dispatch }) => {
      let targetPos = -1
      let targetAttrs: Record<string, unknown> | null = null
      state.doc.descendants((n, pos) => {
        if (targetPos !== -1) return false
        if (n.type.name === 'image' && n.attrs.placeholderId === placeholderId) {
          targetPos = pos
          targetAttrs = n.attrs
          return false
        }
        return true
      })
      if (targetPos === -1 || !targetAttrs) return false
      const attrs = targetAttrs as Record<string, unknown>
      if (dispatch) tr.setNodeMarkup(targetPos, undefined, { ...attrs, uploadingFailed: failed })
      return true
    })
    .run()
}

/** Remove a placeholder — failed upload (card surfaces) or Dismiss. */
export function failCardImagePlaceholder(editor: Editor | null, placeholderId: string): void {
  if (!editor || editor.isDestroyed) return
  editor
    .chain()
    .command(({ tr, state, dispatch }) => {
      let targetPos = -1
      let targetSize = 0
      state.doc.descendants((n, pos) => {
        if (targetPos !== -1) return false
        if (n.type.name === 'image' && n.attrs.placeholderId === placeholderId) {
          targetPos = pos
          targetSize = n.nodeSize
          return false
        }
        return true
      })
      if (targetPos === -1) return false
      if (dispatch) tr.delete(targetPos, targetPos + targetSize)
      return true
    })
    .run()
}
